import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeLayer } from './escape-stack';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import clsx from 'clsx';

// 2026-05-20 — Module-level open-modal counter so stacked modals
// (e.g. dispatch grid Notes → "+ New Note" child) get sequentially
// higher z-indices. Without this, two Modals share `z 1070 / 1080`
// and the *outer* panel ends up sandwiched between the two backdrops,
// so the inner backdrop fails to darken the outer panel and the
// hierarchy reads as a flat overlap. Each Modal captures its own
// stack index when it opens; we step backdrop/panel up by 20 per
// level (1070→1090→1110…) which is well below Drawer (1050/1060)
// won't collide with anything else in the app.
const MODAL_BASE_Z_BACKDROP = 1070;
const MODAL_BASE_Z_PANEL = 1080;
const MODAL_Z_STEP = 20;
let openModalCount = 0;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  closeButton?: boolean;
  // When false, clicking the dimmed overlay outside the modal does NOT
  // close it — the operator must use the close button or footer Cancel.
  // Used by destructive / data-entry surfaces (e.g. NewInvoiceWizard)
  // where an accidental backdrop click would discard in-progress work.
  // Defaults to true (legacy behavior).
  closeOnBackdrop?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-[95vw]',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeButton = true,
  closeOnBackdrop = true,
}: ModalProps) {
  // 2026-05-20 — Per-instance stack index. When the modal opens we
  // capture the current `openModalCount` and increment it; on close
  // / unmount we decrement. Each instance keeps its own slot so
  // children opened later sit above older parents even if both
  // remount.
  const [stackIndex, setStackIndex] = useState<number>(0);
  useEffect(() => {
    if (!open) return;
    const idx = openModalCount;
    openModalCount += 1;
    setStackIndex(idx);
    return () => {
      openModalCount = Math.max(0, openModalCount - 1);
    };
  }, [open]);
  const backdropZ = MODAL_BASE_Z_BACKDROP + stackIndex * MODAL_Z_STEP;
  const panelZ = MODAL_BASE_Z_PANEL + stackIndex * MODAL_Z_STEP;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  // ATL-000537 — Escape goes through the shared layer stack instead of a
  // private `document` listener. Two things were wrong with the old listener:
  // an inner surface (a calendar popover, a nested modal) could not stop this
  // one from also firing, and every stacked Modal closed at once on a single
  // Escape. The stack routes the key to the top-most layer only.
  useEscapeLayer(open, onClose);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
            // 2026-05-20 — Bumped backdrop opacity from /20 to /40
            // so stacked modals (e.g. the dispatch grid Notes popup
            // launching a "+ New Note" child) have enough contrast
            // between the inner backdrop and the outer panel for the
            // hierarchy to read at a glance. The blur stays the same
            // — too much blur on the outer panel makes its grids
            // unreadable while the child popup is open.
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            // Modal sits above Drawer (1050/1060) so a sub-modal opened
            // from inside a drawer (e.g. NEW EQUIPMENT TYPE → EquipmentForm)
            // renders on top of the parent drawer instead of behind it.
            // 2026-05-20: z-index now derived from the per-instance
            // stack index so stacked modals layer correctly.
            style={{ zIndex: backdropZ }}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none" style={{ zIndex: panelZ }}>
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              // 2026-05-20 — Added `shadow-2xl` + a hairline
               // `ring-1 ring-black/5` so the panel reads as an
               // elevated layer instead of a flat white card. This
               // was originally a no-op on a flat dimmed backdrop,
               // but matters now that stacked modals (the dispatch
               // grid Notes popup → "+ New Note" child) need the
               // inner panel to visually pop off the outer one.
              className={clsx(
                'w-full rounded bg-white flex flex-col max-h-[90vh] pointer-events-auto shadow-2xl ring-1 ring-black/5',
                sizeClasses[size]
              )}
            >
              {/* Header — always visible, never scrolls */}
              {(title || closeButton) && (
                <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 rounded-t" style={{ borderBottom: '1px solid var(--vz-border-color)' }}>
                  {title && <h2 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--vz-heading-color)' }}>{title}</h2>}
                  {closeButton && (
                    <button
                      onClick={onClose}
                      className="ml-auto rounded p-1 text-[#878a99] hover:bg-[#F4F6F9] dark:hover:bg-white/10 hover:text-[#495057] dark:hover:text-white transition-colors duration-150"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
              {/* Body — scrolls independently */}
              <div className="flex-1 overflow-y-auto p-6 min-h-0">{children}</div>
              {/* Footer — always visible, never scrolls */}
              {footer && (
                <div className="flex-shrink-0 px-6 py-4" style={{ borderTop: '1px solid var(--vz-border-color)' }}>
                  {footer}
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
